import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type SupportedLanguage = "en" | "ar";

type Translations = Record<string, string>;

type LocalizationContextValue = {
    lang: SupportedLanguage;
    isLoading: boolean;
    error: string | null;
    t: (key: string, fallback?: string) => string;
    setLanguage: (lang: SupportedLanguage) => Promise<void>;
    reload: () => Promise<void>;
};

const LocalizationContext = createContext<LocalizationContextValue | undefined>(undefined);

// Optional preload cache so we can load translations BEFORE React mounts
let cachedPreload:
    | {
          lang: SupportedLanguage;
          translations: Translations;
          error: string | null;
      }
    | null = null;

export function parseLanguageFromPathname(pathname: string): SupportedLanguage {
    const first = pathname.split("/").filter(Boolean)[0];
    if (first === "ar" || first === "en") return first;
    return "en";
}

async function fetchTranslations(lang: SupportedLanguage): Promise<Translations> {
    const baseUrl = import.meta.env.VITE_ResourceJsonFile_URL;
    const url = `${baseUrl}${lang}.json`;
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error(`Failed to load translations (${res.status})`);
    return res.json();
}

// Preload translations before app render (to avoid initial flicker)
export async function preloadLocalization(): Promise<void> {
    const initialLang = parseLanguageFromPathname(window.location.pathname);
    try {
        const data = await fetchTranslations(initialLang);
        cachedPreload = { lang: initialLang, translations: data, error: null };
    } catch (e) {
        cachedPreload = {
            lang: initialLang,
            translations: {},
            error: e instanceof Error ? e.message : "Unknown error",
        };
    }
}

export function LocalizationProvider({ children }: { children: React.ReactNode }) {
    const [lang, setLang] = useState<SupportedLanguage>(() => cachedPreload?.lang ?? parseLanguageFromPathname(window.location.pathname));
    const [translations, setTranslations] = useState<Translations>(cachedPreload?.translations ?? {});
    const [isLoading, setIsLoading] = useState<boolean>(!cachedPreload);
    const [error, setError] = useState<string | null>(cachedPreload?.error ?? null);

    const load = useCallback(async (targetLang: SupportedLanguage) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await fetchTranslations(targetLang);
            setTranslations(data);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Unknown error");
            setTranslations({});
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!cachedPreload) {
            load(lang);
        }
        // Clear cache after first use so future mounts reload as needed
        cachedPreload = null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Apply body classes on language change for LTR/RTL layout (matches NagwaClasses body-class model)
    useEffect(() => {
        const body = document.body;
        if (!body) return;
        body.classList.remove("layout-ltr", "layout-rtl", "app-content-ltr", "app-content-rtl");
        if (lang === "ar") {
            body.classList.add("layout-rtl", "app-content-rtl");
        } else {
            body.classList.add("layout-ltr", "app-content-ltr");
        }
        document.documentElement.lang = lang;
    }, [lang]);

    const t = useCallback(
        (key: string, fallback?: string) => {
            if (!key) return fallback ?? "";
            const value = translations[key];
            if (typeof value === "string") return value;
            return fallback ?? key;
        },
        [translations]
    );

    const setLanguage = useCallback(async (targetLang: SupportedLanguage) => {
        setLang(targetLang);
        await load(targetLang);
    }, [load]);

    const reload = useCallback(async () => {
        await load(lang);
    }, [lang, load]);

    const value = useMemo<LocalizationContextValue>(() => ({
        lang,
        isLoading,
        error,
        t,
        setLanguage,
        reload,
    }), [lang, isLoading, error, t, setLanguage, reload]);

    return (
        <LocalizationContext.Provider value={value}>
            {children}
        </LocalizationContext.Provider>
    );
}

export function useLocalization(): LocalizationContextValue {
    const ctx = useContext(LocalizationContext);
    if (!ctx) throw new Error("useLocalization must be used within LocalizationProvider");
    return ctx;
}


